import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum PortalTextCase {
  DEFAULT = "default",
  UPPERCASE = "uppercase",
  LOWERCASE = "lowercase",
}
export const PortalTextCaseZod = createEnum(PortalTextCase, {
  enumName: "PortalTextCase",
});

export enum PortalShadow {
  NONE = "none",
  SMALL = "small",
  MEDIUM = "medium",
  LARGE = "large",
}
export const PortalShadowZod = createEnum(PortalShadow, {
  enumName: "PortalShadow",
});

const portalBrandingSchema = z.object({
  logoUrl: z
    .string()
    .nullish()
    .describe(
      "URL of the merchant logo displayed in the portal header. Must be an uploaded Redo asset URL, not an external URL.",
    ),
  faviconUrl: z
    .string()
    .nullish()
    .describe(
      "URL of the browser tab favicon. Must be an uploaded Redo asset URL.",
    ),
  backgroundColor: z
    .string()
    .nullish()
    .describe(
      "Solid background color for the portal page. Hex string. Used when no backgroundImageUrl is set.",
    ),
  backgroundImageUrl: z
    .union([z.string().url(), z.literal("")])
    .nullish()
    .describe(
      "URL of the full-page background image. Must be an uploaded Redo asset URL or empty string to clear.",
    ),
  portalBackgroundColor: z
    .string()
    .nullish()
    .describe("Background color of the portal content container. Hex string."),
  linkColor: z
    .string()
    .nullish()
    .describe("Color for hyperlinks within the portal. Hex string."),
});

const portalColorsSchema = z.object({
  primary: z
    .string()
    .nullish()
    .describe(
      "Primary accent/CTA color used for links, highlights, and active states. Hex string e.g. '#3B82F6'.",
    ),
  background: z
    .string()
    .nullish()
    .describe("Page background color. Hex string."),
  error: z
    .string()
    .nullish()
    .describe(
      "Color used for error states and destructive actions. Hex string.",
    ),
});

const portalNavbarSchema = z.object({
  backgroundColor: z
    .string()
    .nullish()
    .describe("Navbar/header background color. Hex string."),
  textColor: z
    .string()
    .nullish()
    .describe("Navbar text and icon color. Hex string."),
});

const portalTypographyFontsSchema = z.object({
  heading: z
    .string()
    .nullish()
    .describe(
      "Font family name for headings (h1–h3). Use exact CSS font-family value e.g. 'Inter', 'Georgia'.",
    ),
  body: z
    .string()
    .nullish()
    .describe("Font family name for body text and UI labels."),
  custom: z
    .array(z.string())
    .nullish()
    .describe("Additional custom font family names to load, if any."),
});

const portalTextColorsSchema = z.object({
  primary: z
    .string()
    .nullish()
    .describe("Primary body text color. Hex string."),
  secondary: z
    .string()
    .nullish()
    .describe("Secondary/muted text color. Hex string."),
});

const portalTypographyFontWeightsSchema = z.object({
  heading: z.number().int().min(100).max(900).nullish(),
  body: z.number().int().min(100).max(900).nullish(),
});

const portalTypographySchema = z.object({
  fonts: portalTypographyFontsSchema.nullish(),
  fontWeights: portalTypographyFontWeightsSchema.nullish(),
  textColors: portalTextColorsSchema.nullish(),
  textCase: PortalTextCaseZod.nullish(),
});

const portalButtonColorsSchema = z.object({
  background: z
    .string()
    .nullish()
    .describe("Button background fill color. Hex string."),
  border: z.string().nullish().describe("Button border color. Hex string."),
  text: z.string().nullish().describe("Button label text color. Hex string."),
  backgroundHover: z
    .string()
    .nullish()
    .describe("Button background color on hover. Hex string."),
  textHover: z
    .string()
    .nullish()
    .describe("Button label color on hover. Hex string."),
});

const portalButtonStyleSchema = z.object({
  font: z.string().nullish(),
  cornerRadius: z
    .number()
    .nullish()
    .describe(
      "Button border radius in pixels. 0 = square corners, higher = more rounded.",
    ),
  borderWidth: z
    .number()
    .nullish()
    .describe("Button border thickness in pixels."),
  padding: z.number().nullish(),
  shadow: PortalShadowZod.nullish().describe("Button drop shadow preset."),
});

const portalButtonVariantSchema = z.object({
  colors: portalButtonColorsSchema.nullish(),
  style: portalButtonStyleSchema.nullish(),
});

const portalButtonsSchema = z.object({
  primary: portalButtonVariantSchema.nullish(),
  secondary: portalButtonVariantSchema.nullish(),
  tertiary: portalButtonVariantSchema.nullish(),
});

const portalInputFieldColorsSchema = z.object({
  background: z.string().nullish(),
  border: z.string().nullish(),
  text: z.string().nullish(),
  error: z.string().nullish(),
});

const portalInputFieldStyleSchema = z.object({
  font: z.string().nullish(),
  cornerRadius: z.number().nullish(),
  borderWidth: z.number().nullish(),
  padding: z.number().nullish(),
  shadow: PortalShadowZod.nullish().describe("Input field drop shadow preset."),
});

const portalInputFieldsSchema = z.object({
  colors: portalInputFieldColorsSchema.nullish(),
  style: portalInputFieldStyleSchema.nullish(),
});

const portalSelectionCardColorsSchema = z.object({
  selectedBackground: z.string().nullish(),
  selectedBorder: z.string().nullish(),
});

const portalGlobalCardSchema = z
  .object({
    borderColor: z
      .string()
      .nullish()
      .describe(
        "Border color for the main portal display card and the login card.",
      ),
    borderWidth: z
      .number()
      .nullish()
      .describe(
        "Border width in pixels for the main portal display card and the login card.",
      ),
    borderRadius: z
      .number()
      .nullish()
      .describe(
        "Corner radius in pixels for the main portal display card and the login card.",
      ),
    shadow: PortalShadowZod.nullish().describe(
      "Drop shadow preset for the main portal display card and the login card.",
    ),
  })
  .describe(
    "Border and shadow customization for the two main portal cards: the login card and the primary display card shown throughout the portal.",
  );

const portalStyleSchema = z.object({
  cornerRadius: z
    .number()
    .nullish()
    .describe(
      "Global corner radius in pixels applied to selection cards and other container elements.",
    ),
  borderWidth: z.number().nullish(),
  colors: portalSelectionCardColorsSchema.nullish(),
  globalCard: portalGlobalCardSchema.nullish(),
});

const loginPageButtonsCustomizationSchema = z
  .object({
    primary: z.object({ colors: portalButtonColorsSchema.nullish() }).nullish(),
    secondary: z
      .object({ colors: portalButtonColorsSchema.nullish() })
      .nullish(),
  })
  .describe(
    "Login-page-only button color overrides. When unset, the general `buttons` customization applies.",
  );

const loginPageInputFieldsCustomizationSchema = z
  .object({ colors: portalInputFieldColorsSchema.nullish() })
  .describe(
    "Login-page-only input field color overrides. When unset, the general `inputFields` customization applies.",
  );

const loginPageCustomizationSchema = z.object({
  colors: z
    .object({
      backToStoreText: z.string().nullish(),
      headingText: z.string().nullish(),
      bodyText: z.string().nullish(),
      loginButtonDisabledBackground: z.string().nullish(),
      loginButtonDisabledText: z.string().nullish(),
    })
    .nullish(),
  text: z
    .object({
      heading: z.string().nullish(),
      subtitle: z.string().nullish(),
      emailLabel: z.string().nullish(),
      orderNumberLabel: z.string().nullish(),
      loginButton: z.string().nullish(),
      giftButton: z.string().nullish(),
      returnPolicyLink: z.string().nullish(),
      registrationTitle: z.string().nullish(),
    })
    .nullish(),
  branding: z
    .object({
      backgroundImage: z.string().nullish(),
      backgroundColor: z
        .string()
        .nullish()
        .describe(
          "Login page background color override. Hex string. When unset, falls back to the general `branding.backgroundColor`.",
        ),
      cardBackground: z.string().nullish(),
      cardOpacity: z.number().min(0).max(1).nullish(),
      cardBlur: z.number().min(0).max(40).nullish(),
      cardCornerRadius: z
        .number()
        .nullish()
        .describe(
          "Login card corner radius override in pixels. When unset, falls back to `style.globalCard.borderRadius`.",
        ),
    })
    .nullish(),
  typography: z
    .object({
      headingFont: z
        .string()
        .nullish()
        .describe(
          "Login-page-only heading font family override. When unset, falls back to `typography.fonts.heading`.",
        ),
      headingFontWeight: z
        .number()
        .int()
        .min(100)
        .max(900)
        .nullish()
        .describe(
          "Login-page-only heading font weight override. When unset, falls back to `typography.fontWeights.heading`.",
        ),
      bodyFont: z
        .string()
        .nullish()
        .describe(
          "Login-page-only body font family override. When unset, falls back to `typography.fonts.body`.",
        ),
      bodyFontWeight: z
        .number()
        .int()
        .min(100)
        .max(900)
        .nullish()
        .describe(
          "Login-page-only body font weight override. When unset, falls back to `typography.fontWeights.body`.",
        ),
    })
    .nullish(),
  buttons: loginPageButtonsCustomizationSchema.nullish(),
  inputFields: loginPageInputFieldsCustomizationSchema.nullish(),
  behavior: z
    .object({
      hideGiftButton: z.boolean().nullish(),
      hideWarrantyRegistration: z.boolean().nullish(),
      hideEmailLogin: z.boolean().nullish(),
    })
    .nullish(),
  alertHtml: z.string().nullish(),
});

const orderDetailsPageCustomizationSchema = z.object({
  text: z
    .object({
      returnButton: z.string().nullish(),
      registerButton: z.string().nullish(),
      warrantyClaimButton: z.string().nullish(),
      shippingClaimButton: z.string().nullish(),
      returnStatusSubtitle: z.string().nullish(),
      exchangeStatusSubtitle: z.string().nullish(),
      storeCreditStatusSubtitle: z.string().nullish(),
    })
    .nullish(),
  behavior: z
    .object({ hideStartReturnButton: z.boolean().nullish() })
    .nullish(),
});

const selectItemsPageCustomizationSchema = z.object({
  text: z
    .object({ heading: z.string().nullish(), subtitle: z.string().nullish() })
    .nullish(),
  warrantyText: z
    .object({ heading: z.string().nullish(), subtitle: z.string().nullish() })
    .nullish(),
  warrantyBehavior: z
    .object({ hideItemPrices: z.boolean().nullish() })
    .nullish(),
});

const itemDetailsPageCustomizationSchema = z.object({
  text: z
    .object({
      heading: z.string().nullish(),
      subtitle: z.string().nullish(),
      addDetailsButton: z.string().nullish(),
      editDetailsButton: z.string().nullish(),
    })
    .nullish(),
  behavior: z
    .object({
      hideAddExchangeItemButton: z.boolean().nullish(),
      hideItemPrices: z.boolean().nullish(),
    })
    .nullish(),
  warrantyText: z
    .object({
      heading: z.string().nullish(),
      subtitle: z.string().nullish(),
      addDetailsButton: z.string().nullish(),
      editDetailsButton: z.string().nullish(),
    })
    .nullish(),
  warrantyBehavior: z
    .object({
      hideAddExchangeItemButton: z.boolean().nullish(),
      hideItemPrices: z.boolean().nullish(),
    })
    .nullish(),
});

const returnTypePageCustomizationSchema = z.object({
  text: z
    .object({
      heading: z.string().nullish(),
      subtitle: z.string().nullish(),
      sameItemTitle: z.string().nullish(),
      sameItemSubtitle: z.string().nullish(),
      variantExchangeTitle: z.string().nullish(),
      variantExchangeSubtitle: z.string().nullish(),
      exchangeGroupsTitle: z.string().nullish(),
      exchangeGroupsSubtitle: z.string().nullish(),
      notExchangeButtonText: z.string().nullish(),
      notExchangeButtonSubtitle: z.string().nullish(),
    })
    .nullish(),
  backorder: z
    .object({
      heading: z.string().nullish(),
      subtitle: z.string().nullish(),
      showDate: z.boolean().nullish(),
    })
    .nullish(),
});

const shipmentMethodTitlesSchema = z.object({
  inStore: z.string().nullish(),
  selfShip: z.string().nullish(),
  blueYonder: z.string().nullish(),
  /** @deprecated Legacy ReturnBear shipment method; retained for existing customizations. */
  returnBear: z.string().nullish(),
  manualLabel: z.string().nullish(),
  dropoff: z.string().nullish(),
});

const shipmentMethodSubtitlesSchema = z.object({
  inStore: z.string().nullish(),
  selfShip: z.string().nullish(),
  blueYonder: z.string().nullish(),
  /** @deprecated Legacy ReturnBear shipment method; retained for existing customizations. */
  returnBear: z.string().nullish(),
  manualLabel: z.string().nullish(),
});

const shipmentMethodPageCustomizationSchema = z.object({
  text: z
    .object({ heading: z.string().nullish(), subtitle: z.string().nullish() })
    .nullish(),
  methodTitles: shipmentMethodTitlesSchema.nullish(),
  methodSubtitles: shipmentMethodSubtitlesSchema.nullish(),
  behavior: z.object({ hideLowCostTag: z.boolean().nullish() }).nullish(),
});

const addressPageCustomizationSchema = z.object({
  behavior: z
    .object({
      requirePhone: z
        .boolean()
        .nullish()
        .describe(
          "When enabled, shoppers must provide a valid phone number before continuing past the address confirmation page.",
        ),
    })
    .nullish(),
});

const confirmationStepTextSchema = z.object({
  title: z.string().nullish(),
  subtitle: z.string().nullish(),
});

const confirmationTextSchema = z.object({
  heading: z.string().nullish(),
  subtitle: z.string().nullish(),
});

const confirmationStepsSchema = z.object({
  title: z.string().nullish(),
  manualReview: confirmationStepTextSchema.nullish(),
  inTransit: confirmationStepTextSchema.nullish(),
  processed: confirmationStepTextSchema.nullish(),
  processedRepair: confirmationStepTextSchema.nullish(),
  dropOffPackage: confirmationStepTextSchema.nullish(),
  greenReturnProcessed: confirmationStepTextSchema.nullish(),
});

const confirmationPageCustomizationSchema = z.object({
  text: confirmationTextSchema.nullish(),
  draftOrder: confirmationTextSchema.nullish(),
  steps: confirmationStepsSchema.nullish(),
  warrantyText: confirmationTextSchema.nullish(),
  warrantyDraftOrder: confirmationTextSchema.nullish(),
  warrantySteps: confirmationStepsSchema.nullish(),
  behavior: z
    .object({ hideWarrantyItemCosts: z.boolean().nullish() })
    .nullish(),
});

const reviewPageCustomizationSchema = z.object({
  text: z
    .object({
      pickupTitle: z.string().nullish(),
      pickupSubtext: z.string().nullish(),
      inStoreTitle: z.string().nullish(),
      inStoreSubtext: z.string().nullish(),
      blueYonderTitle: z.string().nullish(),
      blueYonderSubtext: z.string().nullish(),
      selfShipTitle: z.string().nullish(),
      selfShipSubtext: z.string().nullish(),
      manualLabelTitle: z.string().nullish(),
      manualLabelSubtext: z.string().nullish(),
      itemsOldHeader: z.string().nullish(),
      itemsNewHeader: z.string().nullish(),
      standardExchangeSubHeader: z.string().nullish(),
      instantExchangeSubText: z.string().nullish(),
      adjustmentPositiveName: z.string().nullish(),
      adjustmentNegativeName: z.string().nullish(),
      greenReturnHeader: z.string().nullish(),
      greenReturnSubtext: z.string().nullish(),
      dueLaterSubtext: z.string().nullish(),
    })
    .nullish(),
  behavior: z
    .object({
      showDiscountCodes: z.boolean().nullish(),
      hideWarrantyItemCosts: z.boolean().nullish(),
    })
    .nullish(),
  warrantyText: z
    .object({
      pickupTitle: z.string().nullish(),
      pickupSubtext: z.string().nullish(),
      inStoreTitle: z.string().nullish(),
      inStoreSubtext: z.string().nullish(),
      blueYonderTitle: z.string().nullish(),
      blueYonderSubtext: z.string().nullish(),
      selfShipTitle: z.string().nullish(),
      selfShipSubtext: z.string().nullish(),
      manualLabelTitle: z.string().nullish(),
      manualLabelSubtext: z.string().nullish(),
      itemsOldHeader: z.string().nullish(),
      itemsNewHeader: z.string().nullish(),
      standardExchangeSubHeader: z.string().nullish(),
      instantExchangeSubText: z.string().nullish(),
      adjustmentPositiveName: z.string().nullish(),
      adjustmentNegativeName: z.string().nullish(),
      greenReturnHeader: z.string().nullish(),
      greenReturnSubtext: z.string().nullish(),
      dueLaterSubtext: z.string().nullish(),
    })
    .nullish(),
});

const compensationMethodPageCustomizationSchema = z.object({
  text: z
    .object({
      heading: z.string().nullish().describe("Page heading text."),
      subtitle: z.string().nullish().describe("Page subtitle text."),
      shopNowWithText: z
        .string()
        .nullish()
        .describe(
          "Prefix shown before the exchange value amount (e.g. 'Exchange with '). Trimmed and a trailing space is appended.",
        ),
      exchangeTitle: z
        .string()
        .nullish()
        .describe(
          "Label for the exchange / 'Shop now' compensation method button. Defaults to 'Shop now'.",
        ),
      storeCreditTitle: z
        .string()
        .nullish()
        .describe(
          "Label for the 'Store credit' compensation method button. Defaults to 'Store credit'.",
        ),
      refundTitle: z
        .string()
        .nullish()
        .describe(
          "Label for the refund compensation method button. Defaults to 'Refund'.",
        ),
    })
    .nullish(),
  behavior: z
    .object({ showSuggestedProducts: z.boolean().nullish() })
    .nullish(),
});

const aiExchangeRecommendationsPageCustomizationSchema = z.object({
  text: z
    .object({
      continueButton: z.string().nullish(),
      noThanksButton: z.string().nullish(),
    })
    .nullish(),
});

const purchaseChannelPageCustomizationSchema = z.object({
  text: z
    .object({
      subtitle: z
        .string()
        .nullish()
        .describe(
          "Subtitle shown under the registration title on the purchase-channel screen. Defaults to 'Where did you purchase your product?'.",
        ),
      otherOptionLabel: z
        .string()
        .nullish()
        .describe(
          "Label for the free-text field shown when the shopper selects the 'Other' purchase channel. Defaults to 'Please specify'.",
        ),
      otherOptionPlaceholder: z
        .string()
        .nullish()
        .describe(
          "Placeholder for the 'Other' purchase-channel free-text field. Defaults to 'Enter retailer name'.",
        ),
    })
    .nullish(),
});

const registrationCatalogPageCustomizationSchema = z.object({
  text: z
    .object({
      title: z
        .string()
        .nullish()
        .describe(
          "Heading on the retail registration catalog screen. Defaults to 'Select your item'.",
        ),
      subtitle: z
        .string()
        .nullish()
        .describe(
          "Subtitle shown when registering an item. Defaults to 'Choose the item you would like to register'.",
        ),
      claimSubtitle: z
        .string()
        .nullish()
        .describe(
          "Subtitle shown when the retail flow is claim-only (filing a claim rather than registering). Defaults to 'Choose the item you would like to file a claim on'.",
        ),
      searchPlaceholder: z
        .string()
        .nullish()
        .describe(
          "Placeholder for the product search field. Defaults to 'Search'.",
        ),
      cantFindItemButton: z
        .string()
        .nullish()
        .describe(
          "Label for the 'Can't find your item?' button. Defaults to \"Can't find your item?\".",
        ),
      noProductsFound: z
        .string()
        .nullish()
        .describe(
          "Empty-state message shown when no products match. Defaults to 'No products found.'.",
        ),
    })
    .nullish(),
});

const contactInformationPageCustomizationSchema = z.object({
  text: z
    .object({
      heading: z
        .string()
        .nullish()
        .describe(
          "Heading on the registration contact-information screen. Defaults to 'Contact information'.",
        ),
      subheading: z
        .string()
        .nullish()
        .describe(
          "Subheading on the registration contact-information screen. Defaults to 'Please enter your contact information'.",
        ),
    })
    .nullish(),
});

const registrationAddressPageCustomizationSchema = z.object({
  text: z
    .object({
      heading: z
        .string()
        .nullish()
        .describe(
          "Heading on the registration address screen. Defaults to 'Enter your address'.",
        ),
      subheading: z
        .string()
        .nullish()
        .describe(
          "Subheading on the registration address screen. Defaults to 'We need this information to get applicable products and generate labels if necessary'.",
        ),
    })
    .nullish(),
  supportedCountries: z
    .array(z.string())
    .nullish()
    .describe(
      "ISO 3166-1 alpha-2 country codes a shopper may select in the country dropdown on the registration address screen. When empty or unset, all countries are available.",
    ),
});

const portalPagesCustomizationSchema = z.object({
  login: loginPageCustomizationSchema.nullish(),
  purchaseChannel: purchaseChannelPageCustomizationSchema.nullish(),
  registrationCatalog: registrationCatalogPageCustomizationSchema.nullish(),
  contactInformation: contactInformationPageCustomizationSchema.nullish(),
  registrationAddress: registrationAddressPageCustomizationSchema.nullish(),
  orderDetails: orderDetailsPageCustomizationSchema.nullish(),
  selectItems: selectItemsPageCustomizationSchema.nullish(),
  itemDetails: itemDetailsPageCustomizationSchema.nullish(),
  returnType: returnTypePageCustomizationSchema.nullish(),
  shipmentMethod: shipmentMethodPageCustomizationSchema.nullish(),
  address: addressPageCustomizationSchema.nullish(),
  review: reviewPageCustomizationSchema.nullish(),
  confirmation: confirmationPageCustomizationSchema.nullish(),
  compensationMethod: compensationMethodPageCustomizationSchema.nullish(),
  aiExchangeRecommendations:
    aiExchangeRecommendationsPageCustomizationSchema.nullish(),
});

export const portalCustomizationZodSchema = z.object({
  branding: portalBrandingSchema.nullish(),
  colors: portalColorsSchema.nullish(),
  navbar: portalNavbarSchema.nullish(),
  typography: portalTypographySchema.nullish(),
  buttons: portalButtonsSchema.nullish(),
  inputFields: portalInputFieldsSchema.nullish(),
  style: portalStyleSchema.nullish(),
  pages: portalPagesCustomizationSchema.nullish(),
});

export type PortalCustomization = z.infer<typeof portalCustomizationZodSchema>;
